import { type JSX } from "react";
import type { components } from "../../../api/schema";

type PlotDraft = components["schemas"]["PlotDraft"];
type PlotBeat = components["schemas"]["PlotBeat"];

export type BeatMarkerColumnProps = {
  /**
   * 最新の PlotDraft (1 件)。null / undefined 時はコンポーネント自体を null で返す。
   * beats が null の PlotDraft (Step 4 未実行) も null で返す。
   */
  plotDraft?: PlotDraft | null;
};

const PHASE_LABEL: Record<PlotBeat["phase"], string> = {
  ki: "起",
  sho: "承",
  ten: "転",
  ketsu: "結",
};

const PHASE_ORDER: readonly PlotBeat["phase"][] = ["ki", "sho", "ten", "ketsu"];

/**
 * worldline-palimpsest (Phase 3-1, Task 5.1) palimpsest grid の骨格 beat 縦ライン。
 *
 * PlotDraft.beats[].phase を集計し、ki/sho/ten/ketsu それぞれについて縦ライン 1 本を
 * 描画する。CSS Grid の列として配置され、TraceRow の scene ノード列と重ねて表示される。
 *
 * PlotDraft 不在 (未生成 / API 失敗のフォールバック) の場合は何も描画しない (Req 3.4)。
 */
export function BeatMarkerColumn({ plotDraft }: BeatMarkerColumnProps): JSX.Element | null {
  if (plotDraft === null || plotDraft === undefined) return null;
  const beats = plotDraft.beats;
  if (beats === null || beats === undefined || beats.length === 0) return null;

  // 各 phase に少なくとも 1 つビートが存在するかを集計する (存在しない phase は淡色表示)
  const phasesWithBeat = new Set<PlotBeat["phase"]>();
  for (const b of beats) {
    phasesWithBeat.add(b.phase);
  }

  return (
    <div
      data-testid="palimpsest-beat-marker-column"
      className="absolute inset-0 pointer-events-none flex items-stretch justify-between px-2"
      aria-hidden="true"
    >
      {PHASE_ORDER.map((phase) => {
        const hasBeat = phasesWithBeat.has(phase);
        return (
          <div
            key={phase}
            data-testid={`palimpsest-beat-line-${phase}`}
            data-beat-present={hasBeat ? "true" : "false"}
            className="flex flex-col items-center"
          >
            <span
              className={[
                "text-[10px] mb-1",
                hasBeat ? "text-indigo-300" : "text-slate-600",
              ].join(" ")}
            >
              {PHASE_LABEL[phase]}
            </span>
            <div
              className={[
                "flex-1 w-px",
                hasBeat ? "bg-indigo-400/60" : "bg-slate-700/40",
              ].join(" ")}
            />
          </div>
        );
      })}
    </div>
  );
}
